// Lightweight auth state container. Holds the in-memory access token plus
// a refresh timer. Exposed via React context.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { MeResponse } from "@couplesgames/core-types";
import {
  clearDirectSession,
  hostedUiLogoutUrl,
  loadDirectSession,
  logout as serverLogout,
  profileFromIdToken,
  saveDirectSession,
  startLogin,
  trySilentRefresh,
} from "../lib/auth.js";
import { config } from "../lib/config.js";
import { decodeJwtClaims } from "../lib/jwt.js";

interface Session {
  accessToken: string;
  expiresAtMs: number;
  /** Present in "direct" auth mode (landing); used to derive the profile. */
  idToken?: string;
}

interface AuthState {
  ready: boolean; // false until we've attempted the initial session restore
  session: Session | null;
}

interface AuthContextValue extends AuthState {
  /** Cognito user ID (`sub` claim) of the signed-in user. null when signed out. */
  userId: string | null;
  /** Account profile (name/email/picture) for the signed-in user. */
  profile: MeResponse | null;
  login: (postLoginPath?: string) => Promise<void>;
  logout: () => Promise<void>;
  setSession: (session: Session | null) => void;
  /** Returns a usable access token, refreshing if it's about to expire. */
  getAccessToken: () => Promise<string | null>;
}

function userIdFromSession(session: Session | null): string | null {
  if (!session) return null;
  const claims = decodeJwtClaims(session.accessToken);
  return typeof claims?.sub === "string" ? claims.sub : null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// Refresh ahead of expiry to avoid race conditions on slow networks.
const REFRESH_LEAD_MS = 60_000;

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [state, setState] = useState<AuthState>({ ready: false, session: null });
  const [profile, setProfile] = useState<MeResponse | null>(null);
  const refreshTimer = useRef<number | null>(null);

  const clearTimer = useCallback(() => {
    if (refreshTimer.current !== null) {
      window.clearTimeout(refreshTimer.current);
      refreshTimer.current = null;
    }
  }, []);

  const scheduleRefresh = useCallback(
    (expiresAtMs: number) => {
      clearTimer();
      const delay = Math.max(5_000, expiresAtMs - Date.now() - REFRESH_LEAD_MS);
      refreshTimer.current = window.setTimeout(async () => {
        const next = await trySilentRefresh();
        applySession(next);
      }, delay);
    },
    [clearTimer],
  );

  // Central session application: update state, persist/clear the direct-mode
  // session, and (re)schedule the refresh timer.
  const applySession = useCallback(
    (session: Session | null) => {
      setState({ ready: true, session });
      clearTimer();
      if (config.authMode === "direct") {
        if (session) saveDirectSession(session);
        else clearDirectSession();
      }
      if (session) scheduleRefresh(session.expiresAtMs);
    },
    [clearTimer, scheduleRefresh],
  );

  const setSession = useCallback(
    (session: Session | null) => {
      applySession(session);
    },
    [applySession],
  );

  // Initial session restore on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      let session: Session | null = null;
      if (config.authMode === "direct") {
        session = loadDirectSession();
      } else {
        session = await trySilentRefresh();
      }
      if (cancelled) return;
      applySession(session);
    })();
    return () => {
      cancelled = true;
      clearTimer();
    };
  }, [applySession, clearTimer]);

  // Derive the account profile whenever the session changes. In "direct" mode
  // the ID token carries the profile; in "backend" mode we ask /api/me.
  useEffect(() => {
    let cancelled = false;
    const session = state.session;
    if (!session) {
      setProfile(null);
      return;
    }
    if (config.authMode === "direct") {
      setProfile(session.idToken ? profileFromIdToken(session.idToken) : null);
      return;
    }
    (async () => {
      try {
        const res = await fetch("/api/me", {
          headers: { authorization: `Bearer ${session.accessToken}` },
          credentials: "include",
        });
        if (cancelled) return;
        if (res.ok) setProfile((await res.json()) as MeResponse);
        else setProfile(null);
      } catch {
        if (!cancelled) setProfile(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [state.session]);

  const login = useCallback(async (postLoginPath = "/") => {
    await startLogin(postLoginPath);
  }, []);

  const logout = useCallback(async () => {
    clearTimer();
    await serverLogout();
    if (config.authMode === "direct") clearDirectSession();
    setProfile(null);
    setState({ ready: true, session: null });
    window.location.assign(hostedUiLogoutUrl(config.appOrigin));
  }, [clearTimer]);

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    const current = state.session;
    if (current && current.expiresAtMs - Date.now() > REFRESH_LEAD_MS) {
      return current.accessToken;
    }
    const next = await trySilentRefresh();
    if (next) {
      applySession(next);
      return next.accessToken;
    }
    applySession(null);
    return null;
  }, [applySession, state.session]);

  const userId = useMemo(() => userIdFromSession(state.session), [state.session]);

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, userId, profile, login, logout, setSession, getAccessToken }),
    [state, userId, profile, login, logout, setSession, getAccessToken],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}
